import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Plus, Pencil, Trash2, Upload, X, LogOut } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";

type TeamMember = {
  id: string;
  name: string;
  role: string;
  image_url: string | null;
  image_position: string;
  sort_order: number;
};

const TeamAdmin = () => {
  const { toast } = useToast();
  const { signOut, user } = useAuth();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<TeamMember | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: "", role: "", image_position: "center 25%", sort_order: 0 });
  const [imageFile, setImageFile] = useState<File | null>(null);

  const { data: members = [], isLoading } = useQuery({
    queryKey: ["admin-team"],
    queryFn: async () => {
      const { data, error } = await supabase.from("team_members").select("*").order("sort_order");
      if (error) throw error;
      return data as TeamMember[];
    },
  });

  const uploadImage = async (file: File): Promise<string> => {
    const ext = file.name.split(".").pop();
    const path = `team-${Date.now()}.${ext}`;
    const { error } = await supabase.storage.from("team-images").upload(path, file);
    if (error) throw error;
    const { data } = supabase.storage.from("team-images").getPublicUrl(path);
    return data.publicUrl;
  };

  const saveMutation = useMutation({
    mutationFn: async () => {
      let imageUrl = editing?.image_url || null;
      if (imageFile) imageUrl = await uploadImage(imageFile);

      const payload = {
        name: form.name.trim(),
        role: form.role.trim(),
        image_position: form.image_position.trim() || "center 25%",
        sort_order: form.sort_order,
        image_url: imageUrl,
      };

      if (editing) {
        const { error } = await supabase.from("team_members").update(payload).eq("id", editing.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("team_members").insert(payload);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      toast({ title: editing ? "Member updated" : "Member added" });
      queryClient.invalidateQueries({ queryKey: ["admin-team"] });
      queryClient.invalidateQueries({ queryKey: ["team-members"] });
      resetForm();
    },
    onError: (err: Error) => toast({ title: "Error", description: err.message, variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("team_members").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast({ title: "Member deleted" });
      queryClient.invalidateQueries({ queryKey: ["admin-team"] });
      queryClient.invalidateQueries({ queryKey: ["team-members"] });
    },
    onError: (err: Error) => toast({ title: "Error", description: err.message, variant: "destructive" }),
  });

  const resetForm = () => {
    setEditing(null);
    setCreating(false);
    setImageFile(null);
    setForm({ name: "", role: "", image_position: "center 25%", sort_order: 0 });
  };

  const startEdit = (m: TeamMember) => {
    setEditing(m);
    setCreating(true);
    setForm({ name: m.name, role: m.role, image_position: m.image_position, sort_order: m.sort_order });
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/admin" className="text-muted-foreground hover:text-foreground"><ArrowLeft className="w-5 h-5" /></Link>
            <h1 className="text-lg font-bold text-foreground">Manage Team</h1>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-muted-foreground hidden sm:inline">{user?.email}</span>
            <Button variant="ghost" size="sm" onClick={signOut}><LogOut className="w-4 h-4 mr-1" /> Logout</Button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8">
        {!creating && (
          <>
            <div className="flex justify-between items-center mb-6">
              <p className="text-muted-foreground">{members.length} team members</p>
              <Button onClick={() => setCreating(true)}><Plus className="w-4 h-4 mr-1" /> Add Member</Button>
            </div>

            {isLoading ? (
              <div className="text-center py-12 text-muted-foreground">Loading…</div>
            ) : (
              <div className="space-y-3">
                {members.map((m) => (
                  <motion.div key={m.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex items-center gap-4 bg-card border border-border rounded-lg p-4">
                    {m.image_url && (
                      <img src={m.image_url} alt={m.name} style={{ objectPosition: m.image_position }} className="w-14 h-14 object-cover rounded" />
                    )}
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-foreground truncate">{m.name}</h3>
                      <p className="text-xs text-muted-foreground">{m.role} · order: {m.sort_order}</p>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="ghost" size="icon" onClick={() => startEdit(m)}><Pencil className="w-4 h-4" /></Button>
                      <Button variant="ghost" size="icon" onClick={() => { if (confirm("Delete this member?")) deleteMutation.mutate(m.id); }}>
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </>
        )}

        {creating && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-foreground">{editing ? "Edit Member" : "New Member"}</h2>
              <Button variant="ghost" size="icon" onClick={resetForm}><X className="w-5 h-5" /></Button>
            </div>

            <form onSubmit={(e) => { e.preventDefault(); saveMutation.mutate(); }} className="space-y-4 max-w-2xl">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Name *</Label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required maxLength={200} />
                </div>
                <div className="space-y-2">
                  <Label>Role *</Label>
                  <Input value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} required maxLength={200} />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Image Position</Label>
                  <Input value={form.image_position} onChange={(e) => setForm({ ...form, image_position: e.target.value })} placeholder="center 25%" maxLength={50} />
                  <p className="text-xs text-muted-foreground">CSS object-position (e.g. center 20%)</p>
                </div>
                <div className="space-y-2">
                  <Label>Sort Order</Label>
                  <Input type="number" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: parseInt(e.target.value) || 0 })} />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Photo {!editing && "*"}</Label>
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2 px-4 py-2 border border-input rounded-md cursor-pointer hover:bg-muted text-sm">
                    <Upload className="w-4 h-4" />
                    {imageFile ? imageFile.name : "Choose file"}
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => setImageFile(e.target.files?.[0] || null)} />
                  </label>
                  {editing?.image_url && !imageFile && (
                    <img src={editing.image_url} alt="" style={{ objectPosition: editing.image_position }} className="w-14 h-14 object-cover rounded" />
                  )}
                </div>
              </div>
              <div className="flex gap-3 pt-4">
                <Button type="submit" disabled={saveMutation.isPending}>
                  {saveMutation.isPending ? "Saving…" : editing ? "Update Member" : "Create Member"}
                </Button>
                <Button type="button" variant="outline" onClick={resetForm}>Cancel</Button>
              </div>
            </form>
          </motion.div>
        )}
      </main>
    </div>
  );
};

export default TeamAdmin;
